import { useEffect, useRef } from 'react';
import axios from 'axios';
import { useAppDispatch, useAppSelector } from './redux';
import { clearCredentials, setCredentials } from '../store/slices/authSlice';
import { authApi } from '../services/authApi';

// Key for storing persistent session flag
const PERSISTENT_SESSION_KEY = 'restaurant_persistent_session';

// Token refresh interval for restaurant systems (refresh every 50 minutes for 60-minute tokens)
// Restaurant sessions should be long-lived and persistent
const REFRESH_INTERVAL = 50 * 60 * 1000; // 50 minutes in milliseconds

// Check if token is close to expiring (within 10 minutes for restaurant systems)
const isTokenNearExpiry = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expiry = payload.exp * 1000; // Convert to milliseconds
    const now = Date.now();
    const timeToExpiry = expiry - now;
    
    // Return true if token expires within 10 minutes (more buffer for restaurant operations)
    return timeToExpiry < 10 * 60 * 1000;
  } catch (error) {
    // If we can't parse the token, assume it's expired
    return true;
  }
};

export const useTokenRefresh = () => {
  const dispatch = useAppDispatch();
  const { accessToken, refreshToken: currentRefreshToken, isAuthenticated } = useAppSelector(state => state.auth);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isRefreshingRef = useRef<boolean>(false);

  // Mark session as persistent for restaurant use
  useEffect(() => {
    if (isAuthenticated) {
      localStorage.setItem(PERSISTENT_SESSION_KEY, 'true');
    } else {
      localStorage.removeItem(PERSISTENT_SESSION_KEY);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !accessToken || !currentRefreshToken) {
      // Clear any existing interval if not authenticated
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Function to attempt token refresh
    const attemptRefresh = async () => {
      if (!isAuthenticated || isRefreshingRef.current) return; // Don't refresh if not authenticated or already refreshing
      
      isRefreshingRef.current = true;
      try {
        const response = await authApi.refreshToken({ refreshToken: currentRefreshToken });
        dispatch(setCredentials(response));
      } catch (error) {
        console.error('❌ Token refresh error:', error);
        // Misma regla que baseApi: solo un rechazo 4xx dice que la sesión murió. Sin
        // red o con Render redeployando (5xx) el refresh token sigue siendo válido, y
        // borrarlo deslogueaba a la caja justo cuando se cae el internet — sin forma
        // de volver a entrar hasta que regrese. Se reintenta en el siguiente ciclo.
        const status = axios.isAxiosError(error) ? error.response?.status : undefined;
        if (status !== undefined && status >= 400 && status < 500) {
          dispatch(clearCredentials());
        }
      } finally {
        isRefreshingRef.current = false;
      }
    };

    // Check if current token is near expiry
    if (isTokenNearExpiry(accessToken)) {
      attemptRefresh();
    }

    // Set up periodic refresh
    intervalRef.current = setInterval(() => {
      if (accessToken && isTokenNearExpiry(accessToken)) {
        attemptRefresh();
      }
    }, REFRESH_INTERVAL);

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, accessToken, currentRefreshToken, isAuthenticated, setCredentials, clearCredentials]);

  // Also provide a manual refresh function
  const manualRefresh = async () => {
    if (currentRefreshToken) {
      try {
        const response = await authApi.refreshToken({ refreshToken: currentRefreshToken });
        dispatch(setCredentials(response));
        return true;
      } catch (error) {
        console.error('Manual refresh failed:', error);
        return false;
      }
    }
    return false;
  };

  return { manualRefresh };
};